// src/components/StaggeredMenu/StaggeredMenu.jsx

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom'; // Keep this import for routing
import { gsap } from 'gsap';
import './StaggeredMenu.css';

// This is the component you will import into App.js
const StaggeredMenu = ({
  position = 'right',
  colors = ['#FF0000', '#FFA500'], // Default to your brand colors
  items = [],
  socialItems = [],
  displaySocials = true,
  displayItemNumbering = false,
  className,
  logoUrl,
  menuButtonColor = '#121212', // Corrected default from previous steps
  openMenuButtonColor = '#F5F5F5',
  accentColor = '#FF0000',
  changeMenuColorOnOpen = false,
  onMenuOpen,
  onMenuClose,
  scrolled
}) => {
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  const panelRef = useRef(null);
  const preLayersRef = useRef(null);
  const preLayerElsRef = useRef([]);
  const textInnerRef = useRef(null);
  const iconRef = useRef(null);
  const [textLines] = useState(['Menu', 'Close']);

  const openTlRef = useRef(null);
  const closeTweenRef = useRef(null);
  const spinTweenRef = useRef(null);
  const textCycleAnimRef = useRef(null);
  const colorTweenRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const busyRef = useRef(false);

  // --- No changes needed in the logic ---
  // (useLayoutEffect, buildOpenTimeline, playOpen, etc. are all correct)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panel = panelRef.current;
      const preContainer = preLayersRef.current;
      const icon = iconRef.current;
      const textInner = textInnerRef.current;
      if (!panel || !icon || !textInner) return;
      let preLayers = [];
      if (preContainer) {
        preLayers = Array.from(preContainer.querySelectorAll('.sm-prelayer'));
      }
      preLayerElsRef.current = preLayers;
      const offscreen = position === 'left' ? -100 : 100;
      gsap.set([panel, ...preLayers], { xPercent: offscreen });
      gsap.set(icon, { rotate: 0, transformOrigin: '50% 50%' });
      gsap.set(textInner, { yPercent: 0 });
      if (toggleBtnRef.current) gsap.set(toggleBtnRef.current, { color: menuButtonColor });
    });
    return () => ctx.revert();
  }, [menuButtonColor, position]);
  const buildOpenTimeline = useCallback(() => {
    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return null;
    openTlRef.current?.kill();
    if (closeTweenRef.current) closeTweenRef.current.kill();
    const itemEls = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
    const socialTitle = panel.querySelector('.sm-socials-title');
    const socialLinks = Array.from(panel.querySelectorAll('.sm-socials-link'));
    if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 });
    if (socialTitle) gsap.set(socialTitle, { opacity: 0 });
    if (socialLinks.length) gsap.set(socialLinks, { y: 25, opacity: 0 });
    const tl = gsap.timeline({ paused: true });
    layers.forEach((layer, i) => {
      tl.to(layer, { xPercent: 0, duration: 0.5, ease: 'power4.out' }, i * 0.07);
    });
    const panelInsertTime = layers.length * 0.07;
    tl.to(panel, { xPercent: 0, duration: 0.65, ease: 'power4.out' }, panelInsertTime);
    if (itemEls.length) {
      tl.to(itemEls, { yPercent: 0, rotate: 0, duration: 1, ease: 'power4.out', stagger: { each: 0.1 } }, panelInsertTime + 0.1);
    }
    if (socialTitle || socialLinks.length) {
      const socialsStart = panelInsertTime + 0.2;
      if (socialTitle) tl.to(socialTitle, { opacity: 1, duration: 0.5 }, socialsStart);
      if (socialLinks.length) {
        tl.to(socialLinks, { y: 0, opacity: 1, duration: 0.55, ease: 'power3.out', stagger: { each: 0.08 } }, socialsStart + 0.04);
      }
    }
    return tl;
  }, []);
  const playOpen = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    const tl = buildOpenTimeline();
    if (tl) {
      tl.eventCallback('onComplete', () => { busyRef.current = false; });
      tl.play(0);
    } else {
      busyRef.current = false;
    }
  }, [buildOpenTimeline]);
  const playClose = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    openTlRef.current?.kill();
    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return;
    const all = [...layers, panel];
    const offscreen = position === 'left' ? -100 : 100;
    gsap.to(all, { xPercent: offscreen, duration: 0.32, ease: 'power3.in', overwrite: true, onComplete: () => { busyRef.current = false; } });
  }, [position]);
  const animateIcon = useCallback(opening => {
    spinTweenRef.current?.kill();
    spinTweenRef.current = gsap.to(iconRef.current, { rotate: opening ? 225 : 0, duration: opening ? 0.8 : 0.35, ease: opening ? 'power4.out' : 'power3.inOut' });
  }, []);
  const animateColor = useCallback(opening => {
    colorTweenRef.current?.kill();
    if (changeMenuColorOnOpen) {
      colorTweenRef.current = gsap.to(toggleBtnRef.current, { color: opening ? openMenuButtonColor : menuButtonColor, delay: 0.18, duration: 0.3 });
    }
  }, [openMenuButtonColor, menuButtonColor, changeMenuColorOnOpen]);
  const animateText = useCallback(opening => {
    textCycleAnimRef.current?.kill();
    const targetY = opening ? -50 : 0;
    textCycleAnimRef.current = gsap.to(textInnerRef.current, { yPercent: targetY, duration: 0.6, ease: 'power3.out' });
  }, []);
  const toggleMenu = useCallback(() => {
    if (busyRef.current) return;
    const newOpenState = !openRef.current;
    openRef.current = newOpenState;
    setOpen(newOpenState);
    if (newOpenState) {
      onMenuOpen?.();
      playOpen();
    } else {
      onMenuClose?.();
      playClose();
    }
    animateIcon(newOpenState);
    animateColor(newOpenState);
    animateText(newOpenState);
  }, [playOpen, playClose, animateIcon, animateColor, animateText, onMenuOpen, onMenuClose]);

  return (
    <div
      className={(className ? className + ' ' : '') + 'staggered-menu-wrapper'}
      style={{ '--sm-accent': accentColor, '--sm-toggle-color': menuButtonColor }}
      data-position={position}
      data-open={open || undefined}
      data-scrolled={scrolled || undefined}
    >
      <div ref={preLayersRef} className="sm-prelayers" aria-hidden="true">
        {colors.slice(0, 4).map((c, i) => <div key={i} className="sm-prelayer" style={{ background: c }} />)}
      </div>
      <header className="staggered-menu-header">
        <Link to="/" className="sm-logo" aria-label="Go to Krida Studio Homepage">
          {logoUrl && <img src={logoUrl} alt="Krida Studio Logo" className="sm-logo-img" draggable={false} />}
        </Link>
        <button
          ref={toggleBtnRef}
          className="sm-toggle"
          onClick={toggleMenu}
          type="button"
        >
          <span className="sm-toggle-textWrap" aria-hidden="true">
            <span ref={textInnerRef} className="sm-toggle-textInner">
              {textLines.map((l, i) => <span className="sm-toggle-line" key={i}>{l}</span>)}
            </span>
          </span>
          <span ref={iconRef} className="sm-icon" aria-hidden="true">
            <span className="sm-icon-line" />
            <span className="sm-icon-line" style={{ transform: 'translate(-50%, -50%) rotate(90deg)' }} />
          </span>
        </button>
      </header>
      <aside ref={panelRef} className="staggered-menu-panel">
        <div className="sm-panel-inner">
          <ul className="sm-panel-list" data-numbering={displayItemNumbering || undefined}>
            {/* --- THIS IS THE CORRECTED BLOCK --- */}
            {items?.map((it, idx) => (
              <li className="sm-panel-itemWrap" key={it.label + idx}>
                <Link 
                  className="sm-panel-item" 
                  to={it.link} 
                  aria-label={it.ariaLabel}
                >
                  <span className="sm-panel-itemLabel">{it.label}</span>
                </Link>
              </li>
            ))}
          </ul>
          {displaySocials && socialItems?.length > 0 && (
            <div className="sm-socials">
              <h3 className="sm-socials-title">Socials</h3>
              <ul className="sm-socials-list">
                {socialItems.map((s, i) => (
                  <li key={s.label + i} className="sm-socials-item">
                    <a href={s.link} target="_blank" rel="noopener noreferrer" className="sm-socials-link">
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default StaggeredMenu;